/**
 * The `metadata.failover` object the service writes on every journal entry of
 * an agent step's failover pass (task 1300): on each `llm_call` row and on the
 * step's `completed` or `failed` entry. Read by the Run Explorer journal viewer
 * and step detail panel (task 1301).
 */
export type FailoverReason = 'transport' | 'invalid_reply';

export interface JournalFailover {
  reason: FailoverReason;
  fromProviderKey: string | null;
  fromModelId: string | null;
  fromModelConfigId: string | null;
  primaryAttempts: number | null;
  primaryError: string | null;
}

const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/** The entry's failover record, or null when the entry did not run on failover. */
export function failoverOf(metadata: Record<string, unknown> | null | undefined): JournalFailover | null {
  const raw = metadata?.failover;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if (record.reason !== 'transport' && record.reason !== 'invalid_reply') return null;
  return {
    reason: record.reason,
    fromProviderKey: text(record.fromProviderKey),
    fromModelId: text(record.fromModelId),
    fromModelConfigId: text(record.fromModelConfigId),
    primaryAttempts: typeof record.primaryAttempts === 'number' ? record.primaryAttempts : null,
    primaryError: text(record.primaryError),
  };
}

/** The reason as the run views say it. */
export const FAILOVER_REASON_LABELS: Record<FailoverReason, string> = {
  transport: 'transport error',
  invalid_reply: 'invalid reply',
};

/** "from claude-sonnet-5-5 (transport error)". */
export function failoverFromText(failover: JournalFailover): string {
  return `from ${failover.fromModelId ?? 'the primary Model'} (${FAILOVER_REASON_LABELS[failover.reason]})`;
}
